// Remembers unfinished Upload Service v2 sessions so picking the same file
// again (or a page reload) continues where the upload stopped.
//
// Web: localStorage `uploadV2:<fingerprint>` → { id, uploadToken, partSize,
// partCount, slug, filename, size, lastModified, createdAt, mode, insertIndex,
// notifyGuests, meta, status }. The fingerprint is a local lookup key only
// (evt.live computes its own). Every storage access is wrapped: private
// windows, blocked site data and full quotas just mean "nothing remembered".

export const RESUME_KEY_PREFIX = "uploadV2:";
const DISMISSED_KEY = "uploadV2Dismissed";
const MAX_DISMISSED = 20;

// evt.live resumes `uploading` sessions younger than 7 days and the upload
// token lasts 7 days, so older entries are useless.
export const RESUME_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// cyrb53: a small, stable 53-bit string hash. Not a security boundary.
export function hashString(input, seed = 0) {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let index = 0; index < input.length; index += 1) {
    const code = input.charCodeAt(index);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

export function fingerprintFor({ slug, name, size, lastModified }) {
  const source = [slug, name, size, lastModified ?? ""].join("|");
  return hashString(String(source)).toString(36);
}

const isEntry = (entry) =>
  Boolean(
    entry &&
      typeof entry === "object" &&
      typeof entry.fingerprint === "string" &&
      typeof entry.id === "string" &&
      entry.id &&
      typeof entry.uploadToken === "string" &&
      entry.uploadToken &&
      typeof entry.slug === "string" &&
      typeof entry.filename === "string" &&
      Number.isSafeInteger(entry.size) &&
      Number.isFinite(entry.createdAt)
  );

export function createResumeStore({
  getStorage,
  now = () => Date.now(),
  maxAgeMs = RESUME_MAX_AGE_MS,
} = {}) {
  const storage = () => {
    try {
      return (getStorage && getStorage()) || null;
    } catch (error) {
      return null;
    }
  };

  const readKey = (key) => {
    try {
      const target = storage();
      const raw = target ? target.getItem(key) : null;
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return undefined; // unreadable or corrupt
    }
  };

  const writeKey = (key, value) => {
    try {
      const target = storage();
      if (!target) return false;
      target.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  };

  const removeKey = (key) => {
    try {
      const target = storage();
      if (target) target.removeItem(key);
    } catch (error) {
      // nothing to do
    }
  };

  const keyFor = (fingerprint) => `${RESUME_KEY_PREFIX}${fingerprint}`;

  const expired = (entry) => {
    const age = now() - entry.createdAt;
    if (!(age <= maxAgeMs)) return true;
    const expiresAt =
      typeof entry.expiresAt === "string"
        ? Date.parse(entry.expiresAt)
        : Number(entry.expiresAt);
    return Number.isFinite(expiresAt) && expiresAt > 0 && expiresAt <= now();
  };

  // Returns a usable entry for the key, dropping corrupt or expired ones.
  const usable = (key) => {
    const entry = readKey(key);
    if (entry === null) return null;
    if (
      !isEntry(entry) ||
      key !== keyFor(entry.fingerprint) ||
      expired(entry)
    ) {
      removeKey(key);
      return null;
    }
    return entry;
  };

  return {
    keyFor,

    get(fingerprint) {
      return fingerprint ? usable(keyFor(fingerprint)) : null;
    },

    save(entry) {
      if (!isEntry(entry)) return false;
      return writeKey(keyFor(entry.fingerprint), { v: 1, ...entry });
    },

    update(fingerprint, patch) {
      const entry = this.get(fingerprint);
      if (!entry) return false;
      return writeKey(keyFor(fingerprint), { ...entry, ...patch });
    },

    remove(fingerprint) {
      if (fingerprint) removeKey(keyFor(fingerprint));
    },

    // Sessions remembered for an event, newest first.
    listForSlug(slug) {
      const target = storage();
      if (!target || !slug) return [];
      const keys = [];
      try {
        for (let index = 0; index < target.length; index += 1) {
          const key = target.key(index);
          if (typeof key === "string" && key.startsWith(RESUME_KEY_PREFIX)) {
            keys.push(key);
          }
        }
      } catch (error) {
        return [];
      }
      return keys
        .map(usable)
        .filter((entry) => entry && entry.slug === slug)
        .sort((left, right) => right.createdAt - left.createdAt);
    },

    // Conversion failures the user closed, so they aren't shown again.
    isDismissed(id) {
      const ids = readKey(DISMISSED_KEY);
      return Array.isArray(ids) && ids.includes(id);
    },

    markDismissed(id) {
      if (!id) return false;
      const ids = readKey(DISMISSED_KEY);
      const list = Array.isArray(ids) ? ids.filter((item) => item !== id) : [];
      list.push(id);
      return writeKey(DISMISSED_KEY, list.slice(-MAX_DISMISSED));
    },
  };
}
